import { TextField, Grid } from '@mui/material'
import {
	GridColDef,
	GridRowSelectionModel,
	useGridApiRef,
} from '@mui/x-data-grid-premium'
import {
	GridContainer,
	PageContainer,
	PageTop,
	SearchContainer,
	StyledDataGrid,
} from 'components/ApplicationStyles'
import PagePosition from 'components/PagePosition'
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { alertModal, confirmAlert, timerAlert } from '../../../util/util'
import { koKR } from '@mui/x-data-grid-premium/locales'
import { SpRequestType } from 'constants/types'
import {
	apiSurveyDetailsSave,
	apiSurveyMasterById,
	apiSurveyMasterList,
	apiSurveyMasterSave,
} from 'api/api'
import SurveyPopup from './SurveyPopup'
import {
	SurveyItemType,
	SurveyMasterSearchType,
	SurveyMasterType,
} from 'constants/appType'

const Survey = () => {
	const apiRef = useGridApiRef()
	const [rowSelectionModel, setRowSelectionModel] =
		useState<GridRowSelectionModel>({ type: 'include', ids: new Set() })

	const [disabledAdd] = useState(false)
	const [disabledDel, setDisableDel] = useState(true)
	const [disabledSave, setDisableSave] = useState(true)
	const [disabledSearch] = useState(false)
	const [disabledReset] = useState(true)
	const [disabledExcelDownload] = useState(true)
	const [hideAdd] = useState(false)
	const [hideDel] = useState(true)
	const [hideSave] = useState(false)
	const [hideSearch] = useState(false)
	const [hideReset] = useState(true)
	const [hideExcelDownload] = useState(true)
	const [loading, setLoading] = useState(false)

	const [popupIsOpen, setPopupIsOpen] = useState(false)

	const [selectedData, setSelectedData] = useState<SurveyMasterType | null>(null)
	const [surveyItems, setSurveyItems] = useState<SurveyItemType[]>([])

	const [rows, setRows] = useState<SurveyMasterType[]>([])
	const gridColumns: GridColDef[] = useMemo(
		() => [
			{
				field: 'id',
				headerName: 'ID',
				width: 100,
				editable: false,
				type: 'number',
			},
			{
				field: 'title',
				headerName: '설문 제목',
				minWidth: 300,
				editable: true,
				flex: 1,
				renderCell: (params) => {
					const { id, title } = params.row

					if (id === 0) {
						return <>{title}</>
					}
					return (
						<button
							type="button"
							style={{
								background: 'none',
								border: 'none',
								color: '#1976d2',
								textDecoration: 'underline',
								cursor: 'pointer',
							}}
							onClick={() => surveyPopupOpenAction(params.row)}
						>
							{title}
						</button>
					)
				},
			},
			{
				field: 'rate',
				headerName: '설문 주기',
				width: 100,
				editable: true,
				type: 'singleSelect',
				valueOptions: [
					{ value: 'ONCE', label: '1회' },
					{ value: 'DAILY', label: '일간' },
					{ value: 'WEEKLY', label: '주간' },
					{ value: 'MONTHLY', label: '월간' },
				],
			},
			{
				field: 'isActive',
				headerName: '활성 여부',
				width: 150,
				type: 'boolean',
				editable: true,
			},
			{
				field: 'startDate',
				headerName: '시작일',
				width: 200,
				editable: true,
				type: 'dateTime',
				valueFormatter: (value: any) => {
					if (!value) return ''
					return dayjs(value).format('YYYY-MM-DD HH:mm')
				},
				valueGetter: (value: any) => {
					if (!value) return null
					// 문자열인 경우 Date 객체로 변환
					if (typeof value === 'string') {
						return new Date(value)
					}
					// 이미 Date 객체인 경우 그대로 반환
					return value instanceof Date ? value : new Date(value)
				},
			},
			{
				field: 'endDate',
				headerName: '종료일',
				width: 200,
				editable: true,
				type: 'dateTime',
				valueFormatter: (value: any) => {
					if (!value) return ''
					return dayjs(value).format('YYYY-MM-DD HH:mm')
				},
				valueGetter: (value: any) => {
					if (!value) return null
					// 문자열인 경우 Date 객체로 변환
					if (typeof value === 'string') {
						return new Date(value)
					}
					// 이미 Date 객체인 경우 그대로 반환
					return value instanceof Date ? value : new Date(value)
				},
			},
			{
				field: 'regId',
				headerName: '등록자',
				width: 150,
				editable: false,
			},
			{
				field: 'createdAt',
				headerName: '등록일',
				width: 200,
				editable: false,
				renderCell: (params) => {
					// createdAt보다 updatedAt값의 날짜 차이를 계산해서 upatedAt이 크면 updatedAt을 표시한다 또한 날짜가 오늘이면 시분초만 표기하고 아니면 년월일 시분초 표기
					const createdAt = dayjs(params.row.createdAt)
					const updatedAt = dayjs(params.row.updatedAt)
					const isToday = updatedAt.isSame(dayjs(), 'day')

					if (updatedAt.isAfter(createdAt)) {
						return isToday
							? updatedAt.format('HH:mm:ss')
							: updatedAt.format('YYYY-MM-DD HH:mm:ss')
					} else {
						return isToday
							? createdAt.format('HH:mm:ss')
							: createdAt.format('YYYY-MM-DD HH:mm:ss')
					}
				},
			},
		],
		[rows],
	)

	// 설문 팝업 열기 액션
	const surveyPopupOpenAction = (data: SurveyMasterType) => {
		setSelectedData(data)
		if (data.id) {
			apiSurveyMasterById(data.id).then((res) => {
				setSurveyItems(res[0]?.itemList || [])
				setPopupIsOpen(true)
			})
		} else {
			setSurveyItems([])
			setPopupIsOpen(false)
			alertModal('저장된 설문만 열람할 수 있습니다.', 'warning', () => {})
		}
	}

	const [search, setSearch] = useState<SurveyMasterSearchType>({
		title: '',
		startDate: '',
		endDate: '',
	})

	// 추가
	const actionAdd = () => {
		// id가 0인 행이 이미 존재하는지 확인
		const existIdOfZero = rows.find((row) => row.id === 0)
		if (existIdOfZero) {
			alertModal('새로운 설문을 먼저 저장해주세요.', 'warning', () => {})
			return
		}

		const newRow: SurveyMasterType = {
			crud: 'C',
			id: 0,
			title: '',
			rate: 'ONCE',
			isActive: false,
			startDate: dayjs().format('YYYY-MM-DD HH:mm'),
			endDate: dayjs().add(7, 'day').format('YYYY-MM-DD HH:mm'),
			regId: '',
			modId: '',
			createdAt: '',
			updatedAt: '',
		}
		// grid에 새로운 행 추가
		setRows((prevRows) => [newRow, ...prevRows])
	}

	// 삭제
	const actionDel = () => {
		// 선택된 행의 id 가져오기
		const selectedIds = Array.from(rowSelectionModel.ids) as number[]

		// 선택된 행이 없으면 종료
		if (selectedIds.length === 0) {
			alertModal('저장할 설문을 선택해주세요.', 'warning', () => {})
			return
		}

		// 선택된 행의 데이터 가져오기
		const selectedRows = rows.filter((row) => selectedIds.includes(row.id))

		// selctedRows 중에서 crud가 'C' 이면 바로 삭제, 아니면 확인 모달
		const rowsToDelete = selectedRows.filter((row) => row.crud !== 'C')
		const rowsToRemove = selectedRows.filter((row) => row.crud === 'C')

		confirmAlert(
			'선택한 설문을 삭제하시겠습니까?',
			() => {
				if (rowsToDelete.length > 0) {
					// api로 삭제
					// 삭제 플래그 설정
					rowsToDelete.forEach((row) => (row.crud = 'D'))

					const request: SpRequestType = {
						cmd: 'DELETE',
						jsonText: JSON.stringify(
							rowsToDelete.map((row) => ({
								id: row.id,
								crud: 'D',
							})),
						),
					}
					// 삭제 api 호출
					apiSurveyMasterSave(request).then((res) => {
						console.log('삭제 응답:', res)
						// grid에서 행 제거
						setRows((prevRows) =>
							prevRows.filter((row) => !selectedIds.includes(row.id)),
						)
					})
				}
				if (rowsToRemove.length > 0) {
					// grid에서 행 제거
					setRows((prevRows) =>
						prevRows.filter((row) => !selectedIds.includes(row.id)),
					)
				}
			},
			() => {},
		)
	}

	// 행 업데이트 처리
	const processRowUpdate = (newRow: any, oldRow: any) => {
		console.log('Row updated:', newRow)
		// rows state 업데이트
		setRows((prevRows) =>
			prevRows.map((row) =>
				row.id === oldRow.id
					? {
							...(newRow as SurveyMasterType),
							crud: row.crud === 'C' ? 'C' : 'U',
						}
					: row,
			),
		)
		return newRow
	}

	// 행 업데이트 에러 처리
	const handleProcessRowUpdateError = (error: Error) => {
		console.error('Row update error:', error)
		alertModal('데이터 업데이트 중 오류가 발생했습니다.', 'error', () => {})
	}

	// 저장
	const actionSave = () => {
		// 선택된 행의 id 가져오기
		const selectedIds = Array.from(rowSelectionModel.ids) as number[]

		// 선택된 행이 없으면 종료
		if (selectedIds.length === 0) {
			alertModal('저장할 설문을 선택해주세요.', 'warning', () => {})
			return
		}

		// 선택된 행의 데이터 중에 crud가 'C' 또는 'U'인 데이터 가져오기
		const rowsToSave = rows.filter((row) =>
			selectedIds.includes(row.id) ? row.crud === 'C' || row.crud === 'U' : false,
		)

		console.log(rowsToSave)

		rowsToSave.forEach((row) => {
			// startDate, endDate의 형식을 yyyy-MM-DD HH:mm로 맞춤
			row.startDate = dayjs(row.startDate).format('YYYY-MM-DD HH:mm')
			row.endDate = dayjs(row.endDate).format('YYYY-MM-DD HH:mm')
		})

		console.log(rowsToSave)

		// 저장할 데이터가 없으면 종료
		if (rowsToSave.length === 0) {
			alertModal('저장할 설문이 없습니다.', 'warning', () => {})
			return
		}

		confirmAlert(
			'저장 하시겠습니까?',
			() => {
				const request: SpRequestType = {
					cmd: rowsToSave[0].crud === 'C' ? 'SAVE' : 'UPDATE',
					jsonText: JSON.stringify(
						rowsToSave.map((row) => ({
							crud: row.crud,
							id: row.id === 0 ? undefined : row.id,
							title: row.title,
							rate: row.rate,
							isActive: row.isActive || false,
							startDate: row.startDate,
							endDate: row.endDate,
						})),
					),
				}

				// 저장 api 호출
				apiSurveyMasterSave(request).then((res) => {
					console.log('저장 응답:', res)
					timerAlert(res.data, 'success', () => {
						// 저장 후 다시 조회
						actionSearch()
						setPopupIsOpen(false)
					})
				})
			},
			() => {},
		)
	}

	// 검색
	const actionSearch = () => {
		setLoading(true)
		apiSurveyMasterList(search)
			.then((res) => {
				console.log(res)
				setRows(res)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	// 초기화
	const actionReset = () => {
		// 검색 폼 초기화
		setSearch({
			title: '',
			startDate: '',
			endDate: '',
		})
		// 선택 초기화
		setRowSelectionModel({ type: 'include', ids: new Set() })
		// 데이터 다시 조회
		actionSearch()
	}

	// 키보드 엔터키 처리
	const onKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
		if (e.code === 'Enter' || e.key === 'Enter') {
			e.preventDefault() // 폼 제출 방지
			actionSearch()
		}
	}

	// 폼 제출 방지
	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
	}

	useEffect(() => {
		setDisableSave(rowSelectionModel.ids.size === 0)
		setDisableDel(rowSelectionModel.ids.size === 0)
	}, [rowSelectionModel])

	useEffect(() => {
		actionSearch()
	}, [])

	const handleSave = (items: SurveyItemType[]) => {
		console.log(selectedData)
		console.log('Updated surveyItems:', items)
		if (items.length === 0) {
			alertModal('설문 문항을 하나 이상 추가해야 합니다.', 'warning', () => {})
			return
		}
		console.log(items)
		console.log(JSON.stringify(items))

		confirmAlert(
			'설문 문항을 저장하시겠습니까?',
			() => {
				const request: SpRequestType = {
					cmd: 'SAVE',
					jsonText: JSON.stringify(items),
				}
				apiSurveyDetailsSave(request).then((res) => {
					console.log('설문 문항 저장 응답:', res)
					timerAlert(res.rsMsg, 'success', () => {
						// 저장 후 팝업 닫기
						setPopupIsOpen(false)
						actionSearch()
					})
				})
			},
			() => {},
		)
	}

	return (
		<PageContainer>
			<SurveyPopup
				isOpen={popupIsOpen}
				onClose={() => setPopupIsOpen(false)}
				data={selectedData}
				subData={surveyItems}
				save={handleSave}
			/>
			<PageTop>
				<PagePosition
					actionAdd={actionAdd}
					actionDel={actionDel}
					actionSave={actionSave}
					actionSearch={actionSearch}
					actionReset={actionReset}
					actionExcelDownload={() => {}}
					disabledAdd={disabledAdd}
					disabledDel={disabledDel}
					disabledSave={disabledSave}
					disabledSearch={disabledSearch}
					disabledReset={disabledReset}
					disabledExcelDownload={disabledExcelDownload}
					hideAdd={hideAdd}
					hideDel={hideDel}
					hideSave={hideSave}
					hideSearch={hideSearch}
					hideReset={hideReset}
					hideExcelDownload={hideExcelDownload}
				/>
				<SearchContainer
					onKeyDown={onKeyDown}
					onSubmit={onSubmit}
				>
					<Grid
						container
						spacing={2}
					>
						<Grid size={4}>
							<TextField
								label="제목"
								variant="outlined"
								fullWidth
								value={search.title}
								onChange={(e) => setSearch({ ...search, title: e.target.value })}
							/>
						</Grid>
						<Grid size={4}></Grid>
						<Grid size={4}></Grid>
					</Grid>
				</SearchContainer>
			</PageTop>
			<GridContainer>
				<StyledDataGrid
					apiRef={apiRef}
					columns={gridColumns}
					rows={rows}
					showToolbar
					loading={loading}
					localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
					rowSelectionModel={rowSelectionModel}
					onRowSelectionModelChange={(newRowSelectionModel) => {
						setRowSelectionModel(newRowSelectionModel)
					}}
					disableMultipleRowSelection
					editMode="row"
					processRowUpdate={processRowUpdate}
					onProcessRowUpdateError={handleProcessRowUpdateError}
					initialState={{
						columns: {
							columnVisibilityModel: {
								crud: false,
								id: false,
							},
						},
					}}
				/>
			</GridContainer>
		</PageContainer>
	)
}

export default Survey
